import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  FiPlus, FiSearch, FiFilter, FiMoreVertical,
  FiEdit2, FiTrash2, FiEye, FiX, FiCheck, FiMapPin,
  FiPhone, FiMail, FiCalendar, FiDollarSign, FiClock, FiUsers
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import AlertBox from '../../Components/AlertBox';

// API Configuration
const API_BASE_URL = 'https://slateblue-mallard-430836.hostingersite.com/api';

const Franchises = () => {
  const navigate = useNavigate();
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [alertMessage, setAlertMessage] = useState(null);
  const formRef = useRef(null);

  // Add current user state
  const [currentUser, setCurrentUser] = useState(null);

  // State for Add/Edit
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentFranchiseId, setCurrentFranchiseId] = useState(null);
  const [formData, setFormData] = useState({
    franchiseName: '',
    franchiseEmail: '',
    franchisePhone: '',
    address: '',
    planPrice: '',
    startDate: new Date().toISOString().split('T')[0], // Default to today's date
    endDate: '',
    userlimit: '',
    password: '',
    franchiseLogo: null
  });

  useEffect(() => {
    // Get current user from token (SAME AS COMPANIES)
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log('Decoded JWT token:', decoded);
        setCurrentUser({
          id: decoded.id,
          userId: decoded.id,
          role: decoded.role,
          companyId: decoded.companyId || null,
          accessPermissions: decoded.accessPermissions || [],
          superadmin: decoded.superadmin || null,
          franchise: decoded.franchise || null
        });
      } catch (error) {
        console.error('Error decoding JWT:', error);
      }
    }

    fetchFranchises();
  }, []);

  const fetchFranchises = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/franchises`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Fetched franchises:", response.data);
      setFranchises(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching franchises:", err.response?.data || err.message || err);
      setError('Failed to fetch franchises. You might not be authorized.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    // Handle file upload for franchise logo
    if (name === 'franchiseLogo' && files && files[0]) {
      setFormData({ ...formData, franchiseLogo: files[0] });
      return;
    }

    // Validation for Mobile Number (10 digits only)
    if (name === 'franchisePhone') {
      if (value && (!/^\d*$/.test(value) || value.length > 10)) {
        return;
      }
    }

    // Validation for Franchise Name (Letters only)
    if (name === 'franchiseName') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // Convert email to lowercase
    if (name === 'franchiseEmail') {
      setFormData({ ...formData, [name]: value.toLowerCase() });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if current user is loaded and authorized (SAME AS COMPANIES)
    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      setAlertMessage('❌ You do not have permission to create franchises. Only superadmins and super employees can create franchises.');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setAlertMessage('Access token not found. Please login.');
      return;
    }

    // Enhanced validation
    const requiredFields = {
      franchiseName: 'Franchise Name',
      franchiseEmail: 'Franchise Email',
      franchisePhone: 'Franchise Mobile',
      password: 'Password'
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([field]) => !formData[field] || formData[field].trim() === '')
      .map(([_, label]) => label);

    if (missingFields.length > 0) {
      setAlertMessage(`❌ Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.franchiseEmail)) {
      setAlertMessage('❌ Please enter a valid email address');
      return;
    }

    // Create FormData object
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      // Don't send masked password in edit mode
      if (!(isEditing && key === 'password' && formData[key] === '••••••••')) {
        // Handle file upload separately
        if (key === 'franchiseLogo' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (key !== 'franchiseLogo') {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    // Auto-set superadmin based on current user role (SAME AS COMPANIES)
    if (currentUser.role === 'superadmin' || currentUser.role === 'super_employee') {
      // For super_employee, use their associated superadmin ID
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const superadminId = decoded.superadmin || currentUser.superadmin || currentUser.id;
          formDataToSend.append('superadmin', superadminId);
        } catch (error) {
          console.error('Error decoding token for superadminid:', error);
          formDataToSend.append('superadmin', currentUser.id);
        }
      }
    }

    try {
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/franchises/${currentFranchiseId}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setAlertMessage('✅ Franchise updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/franchises`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setAlertMessage('✅ Franchise saved successfully!');
      }
      fetchFranchises();
      resetForm();
    } catch (err) {
      console.error('Error saving franchise:', err);
      // Show detailed error if available
      const errorMessage = err.response?.data?.message || 'Failed to save franchise';
      setAlertMessage(`❌ ${errorMessage}`);
    }
  };

  // Helper function to format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try to parse and format the date
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Error formatting date:', e);
    }

    return '';
  };

  const handleEdit = (franchise) => {
    // Check authorization (SAME AS COMPANIES)
    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      setAlertMessage('❌ You do not have permission to edit franchises.');
      return;
    }

    const dateValue = franchise.endDate || franchise.duration || '';
    const startDateValue = franchise.startDate || '';

    setIsEditing(true);
    setCurrentFranchiseId(franchise._id);
    setFormData({
      franchiseName: franchise.franchiseName || '',
      franchiseEmail: franchise.franchiseEmail || '',
      franchisePhone: franchise.franchisePhone || '',
      address: franchise.address || '',
      planPrice: franchise.planPrice || '',
      startDate: formatDateForInput(startDateValue) || new Date().toISOString().split('T')[0],
      endDate: formatDateForInput(dateValue) || '',
      userlimit: franchise.userlimit || '',
      password: '••••••••', // Show masked password
      franchiseLogo: null // Don't pre-fill file input
    });
    setShowForm(true);

    // Scroll to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDelete = async (id) => {
    // Check authorization (SAME AS COMPANIES)
    if (!currentUser || !['superadmin', 'super_employee'].includes(currentUser.role)) {
      setAlertMessage('❌ You do not have permission to delete franchises.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this franchise?')) return;

    const token = localStorage.getItem('authToken');
    try {
      await axios.delete(`${API_BASE_URL}/franchises/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchises(franchises.filter(f => f._id !== id));
      setAlertMessage('✅ Franchise deleted successfully!');
    } catch (err) {
      console.error('Error deleting franchise:', err);
      setAlertMessage('❌ Failed to delete franchise');
    }
  };

  const handleViewFranchise = (franchise) => {
    navigate(`/franchise/${franchise._id}/companies`);
  };

  const resetForm = () => {
    setFormData({
      franchiseName: '',
      franchiseEmail: '',
      franchisePhone: '',
      address: '',
      planPrice: '',
      startDate: new Date().toISOString().split('T')[0], // Reset to today's date
      endDate: '',
      userlimit: '',
      password: '',
      franchiseLogo: null
    });
    setIsEditing(false);
    setCurrentFranchiseId(null);
    setShowForm(false);
  };

  const filteredFranchises = franchises.filter(franchise =>
    franchise.franchiseName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    franchise.franchiseEmail?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading franchises...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Franchise Management</h1>
            <p className="text-slate-500 mt-2">Manage all franchises in your system</p>
            {currentUser && (
              <div className="text-sm text-slate-400 mt-1">
                Logged in as: <span className="font-medium">{currentUser.role}</span>
                {currentUser.role === 'super_employee' && currentUser.superadmin &&
                  ` (Associated with Superadmin ID: ${currentUser.superadmin})`}
              </div>
            )}
          </div>

          {/* Only show Add button for authorized users (SAME AS COMPANIES) */}
          {currentUser && ['superadmin', 'super_employee'].includes(currentUser.role) ? (
            <button
              onClick={() => {
                if (showForm) resetForm();
                else setShowForm(true);
              }}
              className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
            >
              {showForm ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
              )}
              {showForm ? 'Cancel' : 'Add Franchise'}
            </button>
          ) : (
            <div className="mt-4 md:mt-0 text-red-600 font-medium">
              ❌ You do not have permission to create franchises. Contact a superadmin.
            </div>
          )}
        </div>

        {/* Add/Edit Form (SAME STRUCTURE AS COMPANIES) */}
        {showForm && currentUser && ['superadmin', 'super_employee'].includes(currentUser.role) ? (
          <div ref={formRef} className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Franchise' : 'Add New Franchise'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Name *</label>
                  <input
                    type="text"
                    name="franchiseName"
                    value={formData.franchiseName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter franchise name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Email *</label>
                  <input
                    type="email"
                    name="franchiseEmail"
                    value={formData.franchiseEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="franchise@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                  <input
                    type="text"
                    name="franchisePhone"
                    value={formData.franchisePhone}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and restrict length to 10
                      if (value.length <= 10) {
                        handleInputChange(e);
                      }
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                    }}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter 10-digit mobile number"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {isEditing ? '' : '*'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={(e) => {
                      // Clear masked password on first edit
                      if (formData.password === '••••••••') {
                        setFormData({ ...formData, password: e.target.value });
                      } else {
                        handleInputChange(e);
                      }
                    }}
                    required={!isEditing}
                    disabled={isEditing && formData.password === '••••••••'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={isEditing ? 'Password is protected' : 'Set a password'}
                  />
                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-1">
                      Password cannot be changed after creation for security reasons.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Franchise Logo</label>
                  <input
                    type="file"
                    name="franchiseLogo"
                    accept="image/*"
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload franchise logo (PNG, JPG, etc.)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                    <input
                      type="number"
                      name="planPrice"
                      value={formData.planPrice}
                      onChange={handleInputChange}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

              </div>

              <div className="md:col-span-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter full address"
                  ></textarea>
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {isEditing ? 'Update Franchise' : 'Create Franchise'}
                </button>
              </div>
            </form>
          </div>
        ) : showForm ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 text-center">
            <p className="text-red-600 font-medium">❌ Access denied. You cannot create or edit franchises.</p>
            <button
              onClick={() => setShowForm(false)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Close
            </button>
          </div>
        ) : null}

        {/* Search Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search franchises by name or email..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Franchise List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Franchise List</h2>
            <p className="text-gray-600 text-sm">{Array.isArray(franchises) ? franchises.length : 0} franchises found</p>
          </div>

          {!Array.isArray(franchises) || franchises.length === 0 ? (
            <div className="p-12 text-center">
              <FiUsers className="h-16 w-16 mx-auto text-gray-400" />
              <p className="mt-4 text-gray-600">No franchises found. Add your first franchise to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredFranchises.map((franchise) => (
                <div key={franchise._id} className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 overflow-hidden group">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-200">
                          {franchise.franchiseName?.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <h3 className="font-bold text-lg text-slate-800 group-hover:text-indigo-600 transition-colors">
                            {franchise.franchiseName}
                          </h3>
                          <span className="text-xs font-medium px-2 py-1 bg-green-50 text-green-600 rounded-lg">
                            Active
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-slate-500 text-sm">
                        <FiMail className="mr-3 text-indigo-400" />
                        <span className="truncate">{franchise.franchiseEmail}</span>
                      </div>
                      <div className="flex items-center text-slate-500 text-sm">
                        <FiPhone className="mr-3 text-indigo-400" />
                        <span>{franchise.franchisePhone}</span>
                      </div>
                      <div className="flex items-center text-slate-500 text-sm">
                        <FiMapPin className="mr-3 text-indigo-400" />
                        <span className="truncate">{franchise.address || 'No address provided'}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 py-4 border-t border-slate-50">
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-xs text-slate-500 mb-1">Plan Price</p>
                        <p className="font-bold text-slate-800 flex items-center">
                          <span className="text-green-500 mr-1">₹</span>
                          {franchise.planPrice}
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-3">
                        <p className="text-xs text-slate-500 mb-1">User Limit</p>
                        <p className="font-bold text-slate-800 flex items-center">
                          <FiUsers className="text-blue-500 mr-1" />
                          {franchise.userlimit}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                    <button
                      onClick={() => handleViewFranchise(franchise)}
                      className="flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      <FiEye className="mr-2" /> View Details
                    </button>
                    <div className="flex gap-2">
                      {/* Only show Edit/Delete for authorized users */}
                      {currentUser && ['superadmin', 'super_employee'].includes(currentUser.role) && (
                        <>
                          <button
                            onClick={() => handleEdit(franchise)}
                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(franchise._id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alert Box */}
        {alertMessage && (
          <AlertBox
            message={alertMessage}
            onClose={() => setAlertMessage(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Franchises;

